import { IProvider } from '@/shared/IProvider';
import { Text, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, FormControl, FormLabel, Input, ModalFooter, HStack, Spinner, Select } from '@chakra-ui/react'
import React from 'react'
import { ErrorMessage } from '../components/ErrorMessage';
import { useOperation } from '../components/useOperation';
import { UserListControl } from './UserListControl';
import { IUserInfo } from '@/shared/IUserInfo';
import { EnableSendInvite } from '../components/EnableSendInvite';
import { UserInfoService } from '../services/UserInfoService';
import { INVITE_TYPE } from '@/shared/constants';
import { makeAcronym } from '@/shared/parse';
import { CourseProviderStorage } from '../services/CourseProviderStorage';

export const ProviderDialog = (props: {
  onClose: () => void;
  provider: IProvider;
  onSave: (provider: IProvider, invite: boolean) => Promise<void>;

  title: string;
  description: string;

  saveLabel: string;
  savingLabel: string;
}) => {

  const [provider, setProvider] = React.useState<IProvider>(props.provider);
  const [invite, setInvite] = React.useState(!props.provider.id);
  const [countryChoices, setCountryChoices] = React.useState<string[]>([]);

  React.useEffect(() => {
    CourseProviderStorage.getFieldChoices('ProviderCountry').then(setCountryChoices);
  }, []);

  const [onSave, errorMessage, isProcessing] = useOperation(async () => {
    await props.onSave(provider, invite);
    props.onClose();
  }, [provider, invite]);

  // const navigate = useNavigate();

  const initialRef = React.useRef(null)

  const isDisabled = !provider?.ProviderName || !provider?.ProviderContact || !provider?.ProviderEmail;

  const onAddProviderUser = async (user: IUserInfo, invite: boolean) => {
    const { ensured } = await UserInfoService.ensureUser(user, invite ? INVITE_TYPE.PROVIDER : INVITE_TYPE.NONE);
    return ensured;
  }

  const acronymPlaceholder = makeAcronym(provider.ProviderName || '');

  return (
    <Modal
      initialFocusRef={initialRef}
      finalFocusRef={initialRef}
      size={{ base: 'full', md: 'lg', lg: 'xl' }}
      isOpen
      onClose={props.onClose}
      preserveScrollBarGap
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxH="90vh" my={{ base: 0, md: 8 }}>
        <ModalHeader>{props.title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto">
          <ErrorMessage error={errorMessage} />
          <Text>{props.description}</Text>

          <FormControl key='ProviderName' isRequired mt='3'>
            <FormLabel size='md'>Provider Name</FormLabel>
            <Input ref={initialRef}
              isDisabled={isProcessing}
              placeholder='Urban Development Lab'
              value={provider?.ProviderName}
              onChange={e => setProvider({ ...provider, ProviderName: e.target.value })}
            />
          </FormControl>

          <FormControl key='ProviderIdentifier' isRequired mt='3'>
            <FormLabel>Provider Identifier</FormLabel>
            <Input
              isDisabled={isProcessing}
              placeholder={ acronymPlaceholder || 'UDL'}
              value={provider.ProviderIdentifier}
              onChange={e => setProvider({ ...provider, ProviderIdentifier: e.target.value })}
            />
            <Text mt='1' fontSize='small' color='fg.subtle'>An acronym to identify the provider with EIT UM.</Text>
          </FormControl>

          <FormControl key='ProviderContact' isRequired mt='3'>
            <FormLabel>Provider key contact name</FormLabel>
            <Input
              isDisabled={isProcessing}
              placeholder='Leo Vector'
              value={provider.ProviderContact}
              onChange={e => setProvider({ ...provider, ProviderContact: e.target.value })}
            />
          </FormControl>

          <FormControl key='ProviderEmail' isRequired mt='3'>
            <FormLabel>Key contact email address</FormLabel>
            <Input
              type='email'
              isDisabled={isProcessing}
              value={provider.ProviderEmail}
              placeholder='leo.vector@urbandevlab.eu'
              onChange={e => setProvider({ ...provider, ProviderEmail: e.target.value })}
            />
          </FormControl>

          <FormControl key='ProviderAddress' mt='3'>
            <FormLabel>Provider Address</FormLabel>
            <Input
              placeholder='707 Tomorrow Avenue, Visionary Town, EU 90909'
              isDisabled={isProcessing}
              value={provider.ProviderAddress}
              onChange={e => setProvider({ ...provider, ProviderAddress: e.target.value })}
            />
            <Text fontSize='small' color='fg.subtle'>Used to auto-fill the questionnaires for this provider</Text>
          </FormControl>

          <FormControl key='ProviderPhone' mt='3'>
            <FormLabel>Provider Phone</FormLabel>
            <Input
              type='tel'
              isDisabled={isProcessing}
              placeholder='+00-321-654-9870'
              value={provider.ProviderPhone}
              onChange={e => setProvider({ ...provider, ProviderPhone: e.target.value })}
            />
            <Text fontSize='small' color='fg.subtle'>Used to auto-fill the questionnaires for this provider</Text>
          </FormControl>

          <FormControl key='ProviderWebsite' mt='3'>
            <FormLabel>Provider Website</FormLabel>
            <Input
              type='url'
              isDisabled={isProcessing}
              placeholder='https://urbandevlab.eu'
              value={provider.ProviderWebsite || ''}
              onChange={e => setProvider({ ...provider, ProviderWebsite: e.target.value })}
            />
            <Text fontSize='small' color='fg.subtle'>Used to auto-fill the questionnaires for this provider</Text>
          </FormControl>

          <FormControl key='ProviderCountry' mt='3'>
            <FormLabel>Provider Country</FormLabel>
            <Select
              isDisabled={isProcessing}
              placeholder={'Select country...'}
              value={provider.ProviderCountry || ''}
              onChange={e => setProvider({ ...provider, ProviderCountry: e.target.value })}
            >
              {countryChoices.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </Select>
            <Text fontSize='small' color='fg.subtle'>Used to auto-fill the questionnaires for this provider. Additional countries can be added in SharePoint.</Text>
          </FormControl>

          <FormControl key='ProviderUsers' mt='3'>
            <FormLabel>Provider Users</FormLabel>
            <UserListControl
              users={provider.ProviderUsers ?? []}
              enableAdd={true}
              onAddUser={onAddProviderUser}
              onSetUsers={(users: IUserInfo[]) => setProvider({ ...provider, ProviderUsers: users })}
              title='Add Provider User'
              description='This user will be associated with this provider.'
            />
          </FormControl>

          <EnableSendInvite
            isDisabled={isProcessing}
            sendInviteChecked={invite}
            setSendInviteChecked={setInvite}
            description='Send an invitation to the key contact to join the EIT UM Label portal.'
          />

        </ModalBody>

        <ModalFooter>
          <HStack spacing={4}>
            <Button variant='solid' isDisabled={isDisabled || isProcessing} onClick={onSave}>
              <HStack spacing={2}>{isProcessing && <Spinner />}<Text>{isProcessing ? props.savingLabel : props.saveLabel}</Text></HStack>
            </Button>
            <Button variant='outline' onClick={props.onClose}>Cancel</Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}